import { FC, ReactNode } from 'react';
import { Button, Card, Col, Divider, Row, Space } from 'antd';
import { LogIn } from 'lucide-react';
import { TbUserEdit } from 'react-icons/tb';

import { MacOSIconImage } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import UserDropdownButton from '@/shared/components/buttons/UserDropdownButton';
import UserDropdown from '@/shared/components/dropdowns/UserDropdown';
import { PlatformDotWave } from '@/shared/components/layouts/backgrounds';
import { ThemeSegmented } from '@/shared/components/segmented';
import { useUserStore } from '@/stores';
import { centerComponentInMiddleofViewWithoutAlignItems } from '@/utilities';

import PublicPageContextMenu from '../PublicPageContextMenu';

interface PublicConsentShellProps {
    children: ReactNode;
}

export const PublicConsentShell: FC<PublicConsentShellProps> = ({ children }) => {
    const { token } = useAntDesignTheme();

    return (
        <PublicPageContextMenu aboutUrl='https://countercyclical.com'>
            <main
                style={{
                    backgroundColor: token.colorBgContainer,
                    boxSizing: 'border-box',
                    flex: '1 1 auto',
                    height: '100%',
                    // viewport-layout-allow: this shared shell is the viewport boundary for public consent routes.
                    minHeight: '100dvh',
                    overflow: 'auto',
                    padding: 'clamp(1rem, 4vw, 2rem)',
                    position: 'relative',
                    width: '100%',
                    ...centerComponentInMiddleofViewWithoutAlignItems,
                    alignItems: 'center',
                }}
            >
                <PlatformDotWave />
                <div style={{ maxWidth: '30rem', position: 'relative', width: '100%', zIndex: 1 }}>{children}</div>
            </main>
        </PublicPageContextMenu>
    );
};

interface PublicConsentCardProps {
    children: ReactNode;
    showRegistration?: boolean;
}

export const PublicConsentCard: FC<PublicConsentCardProps> = ({ children, showRegistration = false }) => {
    const { selectedUser: user } = useUserStore();
    const { token } = useAntDesignTheme();
    const isLoggedIn = Boolean(user?.id);
    const appOrigin = import.meta.env.PROD ? 'https://dashboard.countercyclical.io' : 'http://localhost:3000';

    return (
        <Card
            styles={{ header: { padding: '0 0.75rem' } }}
            title={
                <Row align='middle' wrap={false}>
                    <Col flex='none'>
                        <a href='https://countercyclical.com' target='_blank' rel='noopener noreferrer'>
                            <img
                                src={MacOSIconImage}
                                alt='Countercyclical'
                                height={36}
                                width={36}
                                style={{ cursor: 'pointer', marginTop: '0.25rem' }}
                            />
                        </a>
                    </Col>
                    <Col flex='auto' />
                    <Col flex='none'>
                        {isLoggedIn ? (
                            <UserDropdown>
                                <UserDropdownButton />
                            </UserDropdown>
                        ) : showRegistration ? (
                            <Space.Compact>
                                <Button onClick={() => (window.location.href = `${appOrigin}/login`)}>
                                    <LogIn size='1rem' />
                                    Login
                                </Button>
                                <Button onClick={() => (window.location.href = `${appOrigin}/register`)}>
                                    <TbUserEdit size='1rem' />
                                    Register
                                </Button>
                            </Space.Compact>
                        ) : (
                            <Button onClick={() => (window.location.href = `${appOrigin}/login`)}>
                                <LogIn size='1rem' />
                                Login
                            </Button>
                        )}
                    </Col>
                </Row>
            }
            style={{
                backgroundColor: token.colorBgBase,
                borderRadius: token.borderRadiusLG * 2,
                boxShadow: token.boxShadow,
                margin: 'auto',
                maxWidth: 480,
                minWidth: 0,
                width: '100%',
            }}
            suppressContentEditableWarning
        >
            {children}
            <Divider style={{ margin: '1rem 0' }} />
            <div style={{ textAlign: 'center', width: '100%' }}>
                <ThemeSegmented />
            </div>
        </Card>
    );
};
